<template>
  <div class="dashboard">
    <!-- Mensagem se nenhum tipo selecionado -->
    <v-fade-transition>
      <v-alert v-if="selectedProjectType === null" type="info" variant="tonal" class="mb-4 border-opacity-50" border="start" elevation="1">
        <template v-slot:prepend>
          <v-icon size="large">mdi-information</v-icon>
        </template>
        {{ $t('dashboard.selectType') }}
      </v-alert>
    </v-fade-transition>

    <!-- Header do Dashboard: tipo + sottogruppi integrati -->
    <v-card class="mb-4 rounded-lg" elevation="2">
      <div class="d-flex pa-2" style="gap: 8px;">
        <div
          v-for="type in projectTypeSummary"
          :key="type.id"
          class="flex-grow-1"
          style="min-width: 0;"
        >
          <!-- Pulsante tipo principale -->
          <v-btn
            :color="selectedProjectType === type.id ? 'primary' : 'grey-lighten-4'"
            :variant="selectedProjectType === type.id ? 'elevated' : 'flat'"
            block
            size="large"
            class="text-none mb-0 project-type-btn"
            :class="{ 'active-type': selectedProjectType === type.id }"
            :style="selectedProjectType === type.id && groupSummary.length > 0 ? 'border-bottom-left-radius: 0; border-bottom-right-radius: 0;' : 'border-radius: 8px;'"
            @click="selectProjectType(type.id)"
          >
            <span class="mr-2 text-h5">{{ type.icon }}</span>
            <span class="font-weight-bold text-subtitle-1" :class="selectedProjectType === type.id ? 'text-white' : 'text-grey-darken-3'">{{ type.descr }}</span>
            <v-chip
              size="small"
              :color="selectedProjectType === type.id ? 'white' : 'grey-darken-1'"
              :text-color="selectedProjectType === type.id ? 'primary' : 'white'"
              class="ml-3 font-weight-bold"
              variant="elevated"
              elevation="1"
            >
              {{ type.count }}
            </v-chip>
            <v-badge
              v-if="type.warningCount > 0"
              :content="type.warningCount"
              color="orange-darken-1"
              inline
              class="ml-2"
              :title="$t('dashboard.followUpDueToday')"
            ></v-badge>
            <v-badge
              v-if="type.overdueCount > 0"
              :content="type.overdueCount"
              color="red-darken-1"
              inline
              class="ml-1"
              :title="$t('dashboard.followUpOverdue')"
            ></v-badge>
          </v-btn>

          <!-- Sottogruppi (visibili solo se questo tipo è selezionato) -->
          <v-expand-transition>
            <div
              v-if="selectedProjectType === type.id && groupSummary.length > 0"
              class="d-flex bg-grey-lighten-4"
              style="gap: 1px; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; overflow: hidden; border: 1px solid rgb(var(--v-theme-primary)); border-top: none;"
            >
              <v-btn
                v-for="group in groupSummary"
                :key="group.key"
                :color="selectedGroup === group.key ? 'primary' : 'transparent'"
                :variant="selectedGroup === group.key ? 'flat' : 'text'"
                class="text-none flex-grow-1 rounded-0"
                size="small"
                height="36"
                @click="selectGroup(group.key)"
              >
                <span :class="selectedGroup === group.key ? 'text-white' : 'text-grey-darken-3'">{{ $t('dashboard.groups.' + group.key, group.key) }}</span>
                <v-chip
                  size="small"
                  :color="selectedGroup === group.key ? 'white' : 'grey-darken-1'"
                  :text-color="selectedGroup === group.key ? 'primary' : 'white'"
                  class="ml-2 font-weight-bold"
                  variant="elevated"
                  elevation="1"
                >
                  {{ group.count }}
                </v-chip>
                <v-badge v-if="group.warningCount > 0" :content="group.warningCount" color="orange-darken-1" inline class="ml-1"></v-badge>
                <v-badge v-if="group.overdueCount > 0" :content="group.overdueCount" color="red-darken-1" inline class="ml-1"></v-badge>
              </v-btn>
            </div>
          </v-expand-transition>
        </div>
      </div>
    </v-card>

    <!-- Pipeline degli status: barra di distribuzione proporzionale + un chip per status, in ordine
         di flusso. Gli status a zero restano nascosti dietro "+N vazios". -->
    <v-expand-transition>
      <div v-if="selectedProjectType !== null" class="pipeline mb-4">
        <div v-if="pipelineSegments.length" class="pipeline__bar">
          <span
            v-for="seg in pipelineSegments"
            :key="seg.id"
            class="pipeline__seg"
            :class="{ 'pipeline__seg--active': selectedStatus === seg.id }"
            :style="{ flexGrow: seg.count, backgroundColor: seg.color }"
            :title="`${seg.icon || ''} ${seg.descr}: ${seg.count}`.trim()"
            @click="selectStatus(seg.id)"
          ></span>
        </div>
        <div class="d-flex flex-wrap align-center ga-2">
          <v-chip
            v-for="status in visibleStatusChips"
            :key="status.id"
            size="small"
            variant="outlined"
            class="pipeline__chip"
            :class="{ 'pipeline__chip--active': selectedStatus === status.id, 'pipeline__chip--empty': status.count === 0 }"
            :style="statusChipStyle(status)"
            :disabled="status.count === 0"
            @click="selectStatus(status.id)"
          >
            <span class="pipeline__dot mr-2" :style="{ backgroundColor: status.color }"></span>
            <span class="mr-1">{{ status.icon }}</span>
            <span class="text-truncate" :title="status.descr">{{ status.descr }}</span>
            <span class="pipeline__count ml-2">{{ status.count }}</span>
            <span v-if="status.warningCount > 0" class="pipeline__pill pipeline__pill--warn ml-1" :title="$t('dashboard.followUpDueToday')">{{ status.warningCount }}</span>
            <span v-if="status.overdueCount > 0" class="pipeline__pill pipeline__pill--over ml-1" :title="$t('dashboard.followUpOverdue')">{{ status.overdueCount }}</span>
          </v-chip>
          <v-chip
            v-if="emptyStatusCount > 0"
            size="small"
            variant="outlined"
            class="pipeline__chip pipeline__chip--more"
            @click="showEmptyStatuses = !showEmptyStatuses"
          >
            {{ showEmptyStatuses ? $t('dashboard.hideEmpty') : $t('dashboard.emptyStatuses', { n: emptyStatusCount }) }}
          </v-chip>
        </div>
      </div>
    </v-expand-transition>

    <!-- Tabela de Projetos: la card, la toolbar e i filtri attivi stanno nel componente -->
    <ProjectsTable ref="projectsTable" @project-saved="onProjectSaved" />

    <!-- Quick Actions FAB: solo su schermi piccoli, su desktop il bottone sta nella toolbar della tabella -->
    <v-btn
      v-if="$vuetify.display.smAndDown && canCreateProject"
      icon="mdi-plus"
      color="primary"
      size="large"
      elevation="8"
      class="floating-fab"
      @click="openCreateProject"
    ></v-btn>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useProjectStore } from '../stores/projects.js'
import ProjectsTable from '../components/ProjectsTable.vue'
import { useWorkflowAccess } from '../composables/useWorkflowAccess.js'
import { followUpState } from '../utils/followUp.js'

const projectStore = useProjectStore()
const { canCreateProject } = useWorkflowAccess()
const projectsTable = ref()
const selectedProjectType = ref(null)
const selectedStatus = ref(null)
const selectedGroup = ref(null)
const isRefreshing = ref(false)
const showEmptyStatuses = ref(false)

// Computed per riepilogo dei tipi di progetto (Importação/Exportação)
const projectTypeSummary = computed(() => {
  const typeCounts = {}
  const typeWarning = {}
  const typeOverdue = {}

  // Inizializza contatori per tutti i tipi
  projectStore.projectTypes.forEach(type => {
    typeCounts[type.id] = 0
    typeWarning[type.id] = 0
    typeOverdue[type.id] = 0
  })

  // scopedProjects e non allProjects: con il filtro per profilo attivo, contatori, pipeline e
  // tabella devono raccontare la stessa storia.
  const projectsToCount = projectStore.scopedProjects
  projectsToCount.forEach(project => {
    const typeId = parseInt(project.projectType)
    if (typeCounts.hasOwnProperty(typeId)) {
      typeCounts[typeId]++

      // Follow-up in scadenza oggi (warning) o già scaduto (overdue). Gli stati senza
      // follow-up (i terminali, tipo "Pago") non contano, anche con un reminderDate vecchio.
      const statusInfo = projectStore.getStatusInfo(String(project.activeStatus), project.projectType)
      const state = followUpState(project, statusInfo)
      if (state === 'today') typeWarning[typeId]++
      else if (state === 'overdue') typeOverdue[typeId]++
    }
  })

  // Ritorna array con info dei tipi e contatori
  return projectStore.projectTypes.map(type => ({
    ...type,
    count: typeCounts[type.id] || 0,
    warningCount: typeWarning[type.id] || 0,
    overdueCount: typeOverdue[type.id] || 0,
    icon: type.id === 0 ? '📥' : '📤' // Importação / Exportação
  }))
})

// Label del tipo di progetto selezionato
const selectedProjectTypeLabel = computed(() => {
  if (selectedProjectType.value === null) return ''
  const type = projectStore.projectTypes.find(t => t.id === selectedProjectType.value)
  return type?.descr || ''
})

// Computed para resumo dos status (filtrato per tipo selezionado)
const filteredStatusSummary = computed(() => {
  if (selectedProjectType.value === null) return []
  
  const statusCounts = {}
  const warningFollowUpCounts = {}
  const overdueFollowUpCounts = {}
  
  // Ottieni la lista di status per il tipo selezionato
  const statusListForType = projectStore.getStatusListForType(selectedProjectType.value)
  
  // Inizializza contatori per tutti gli status del tipo selezionato
  statusListForType.forEach(status => {
    statusCounts[status.id] = 0
    warningFollowUpCounts[status.id] = 0
    overdueFollowUpCounts[status.id] = 0
  })
  
  // scopedProjects e non allProjects: con il filtro per profilo attivo, contatori, pipeline e
  // tabella devono raccontare la stessa storia.
  const projectsToCount = projectStore.scopedProjects
  projectsToCount.forEach(project => {
    const projectTypeId = parseInt(project.projectType)
    const statusId = parseInt(project.activeStatus)
    
    // Conta solo se il tipo corrisponde
    if (projectTypeId === selectedProjectType.value) {
      if (statusCounts.hasOwnProperty(statusId)) {
        statusCounts[statusId]++
        
        // Stesse regole del riepilogo per tipo, sullo stato di questo processo
        const statusInfo = projectStore.getStatusInfo(String(statusId), selectedProjectType.value)
        const state = followUpState(project, statusInfo)
        if (state === 'today') warningFollowUpCounts[statusId]++
        else if (state === 'overdue') overdueFollowUpCounts[statusId]++
      }
    }
  })
  
  // Ritorna array con info degli status e contatori
  return statusListForType.map(status => ({
    ...status,
    count: statusCounts[status.id] || 0,
    warningCount: warningFollowUpCounts[status.id] || 0,
    overdueCount: overdueFollowUpCounts[status.id] || 0
  }))
})

// Riepilogo per gruppo: contatori aggregati degli status per gruppo
const groupSummary = computed(() => {
  if (selectedProjectType.value === null) return []

  // Ottieni le chiavi dei gruppi nell'ordine in cui appaiono nella statusList
  const groupOrder = []
  const seenGroups = new Set()
  const statusListForType = projectStore.getStatusListForType(selectedProjectType.value)
  statusListForType.forEach(s => {
    const g = s.group
    if (g && !seenGroups.has(g)) {
      seenGroups.add(g)
      groupOrder.push(g)
    }
  })

  return groupOrder.map(groupKey => {
    const statusesInGroup = filteredStatusSummary.value.filter(s => s.group === groupKey)
    return {
      key: groupKey,
      count: statusesInGroup.reduce((sum, s) => sum + s.count, 0),
      warningCount: statusesInGroup.reduce((sum, s) => sum + s.warningCount, 0),
      overdueCount: statusesInGroup.reduce((sum, s) => sum + s.overdueCount, 0)
    }
  })
})

// Status visibili: filtrati per gruppo selezionato (se applicabile)
const visibleStatusSummary = computed(() => {
  if (selectedGroup.value === null) return filteredStatusSummary.value
  return filteredStatusSummary.value.filter(s => s.group === selectedGroup.value)
})

// Segmenti della barra: solo gli status con almeno un processo, larghezza proporzionale al conteggio
const pipelineSegments = computed(() => visibleStatusSummary.value.filter(s => s.count > 0))

// Chip mostrati: quelli con processi; i vuoti compaiono solo su richiesta
const visibleStatusChips = computed(() =>
  showEmptyStatuses.value ? visibleStatusSummary.value : visibleStatusSummary.value.filter(s => s.count > 0)
)

const emptyStatusCount = computed(() => visibleStatusSummary.value.filter(s => s.count === 0).length)

// I colori degli status in cfg.json sono hex: per la tinta del chip attivo serve la versione con alpha
const hexToRgba = (hex, alpha) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '')
  if (!m) return hex
  const n = parseInt(m[1], 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

// Chip attivo: tinta leggera + bordo nel colore dello status, testo scuro (il colore pieno con
// testo bianco non reggeva il contrasto sugli status chiari)
const statusChipStyle = (status) => {
  if (selectedStatus.value !== status.id) return undefined
  return { backgroundColor: hexToRgba(status.color, 0.16), borderColor: status.color }
}

// Watch to auto-deselect status if count becomes 0
watch(filteredStatusSummary, (newSummary) => {
  if (selectedStatus.value !== null) {
    const currentStatus = newSummary.find(s => s.id === selectedStatus.value)
    if (currentStatus && currentStatus.count === 0) {
      selectedStatus.value = null
      projectStore.setStatusFilter(null)
    }
  }
})

// Methods
const selectProjectType = (typeId) => {
  const normalizedTypeId = typeId !== undefined ? typeId : null
  selectedProjectType.value = normalizedTypeId
  selectedStatus.value = null
  selectedGroup.value = null
  projectStore.setProjectTypeFilter(normalizedTypeId)
  projectStore.setStatusFilter(null)
  projectStore.setGroupFilter(null)
}

const selectGroup = (groupKey) => {
  if (selectedGroup.value === groupKey) {
    // Toggle: deseleziona il gruppo
    selectedGroup.value = null
    projectStore.setGroupFilter(null)
  } else {
    selectedGroup.value = groupKey
    projectStore.setGroupFilter(groupKey)
    // Deseleziona lo status specifico se appartiene a un altro gruppo
    if (selectedStatus.value !== null) {
      const statusListForType = projectStore.getStatusListForType(selectedProjectType.value)
      const currentStatusConfig = statusListForType.find(s => s.id === selectedStatus.value)
      if (currentStatusConfig?.group !== groupKey) {
        selectedStatus.value = null
        projectStore.setStatusFilter(null)
      }
    }
  }
}

const selectStatus = (statusId) => {
  // Toggle: se clicco sullo stesso status, lo deseleziono
  if (selectedStatus.value === statusId) {
    selectedStatus.value = null
    projectStore.setStatusFilter(null)
  } else {
    selectedStatus.value = statusId
    projectStore.setStatusFilter(statusId)
  }
  // Non ricarica i dati, usa solo il filtro locale
}

const openCreateProject = () => {
  if (projectsTable.value) {
    projectsTable.value.openCreateDialog()
  }
}

const onProjectSaved = () => {
  // Atualizar dados quando um projeto for salvo (criado ou editado)
  // Mantém o filtro de status atual
  projectStore.loadProjects(projectStore.currentPage, projectStore.statusFilter)
}

// Refresh manuale. La lista si aggiorna da sola con gli eventi dell'hub: questo pulsante resta
// come via di fuga per quando il canale e' offline e l'indicatore in app bar e' grigio.
const manualRefresh = async () => {
  isRefreshing.value = true
  try {
    await projectStore.loadProjects(projectStore.currentPage, projectStore.statusFilter)
  } catch (error) {
    console.error('Error during manual refresh:', error)
  } finally {
    isRefreshing.value = false
  }
}

// Watch per sincronizzare i card quando cambiano i filtri dalla tabella
watch(() => projectStore.statusFilter, (newStatusFilter) => {
  // Aggiorna il selectedStatus della dashboard quando cambia il filtro nello store
  selectedStatus.value = newStatusFilter
})

// Il gruppo può essere tolto anche dal chip nella toolbar della tabella
watch(() => projectStore.groupFilter, (newGroupFilter) => {
  selectedGroup.value = newGroupFilter ?? null
})

watch(() => projectStore.projectTypeFilter, (newTypeFilter) => {
  // Aggiorna il selectedProjectType della dashboard quando cambia il filtro nello store
  selectedProjectType.value = newTypeFilter
})

// Lifecycle
onMounted(async () => {
  // Sincronizza lo stato locale con lo store
  selectedProjectType.value = projectStore.projectTypeFilter
  selectedStatus.value = projectStore.statusFilter

  // Carregar configurações e projetos iniciais
  if (!projectStore.statusList.length) {
    await projectStore.loadConfigurations()
  }
  
  if (!projectStore.projects.length) {
    await projectStore.loadProjects()
  }

  // Ascolta eventi di refresh dalla toolbar
  window.addEventListener('dashboard-refresh', manualRefresh)
})

onUnmounted(() => {
  window.removeEventListener('dashboard-refresh', manualRefresh)
})
</script>

<style scoped>
.dashboard {
  padding: 16px;
  max-width: 100%;
  margin: 0 auto;
  background-color: rgb(var(--v-theme-background));
}

.project-type-btn {
  transition: all 0.3s ease;
}

.project-type-btn.active-type {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1) !important;
}

/* ---- pipeline degli status ---- */
.pipeline {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  padding: 10px 12px 8px;
}

.pipeline__bar {
  display: flex;
  gap: 2px;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.pipeline__seg {
  display: block;
  flex: 1 1 0;
  min-width: 3px;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.pipeline__bar:hover .pipeline__seg:not(:hover) {
  opacity: 0.55;
}

.pipeline__seg--active {
  outline: 2px solid rgba(var(--v-theme-on-surface), 0.85);
  outline-offset: -2px;
}

.pipeline__chip {
  border-color: transparent;
  background: rgba(var(--v-theme-on-surface), 0.05);
  color: rgba(var(--v-theme-on-surface), 0.87);
  cursor: pointer;
  max-width: 100%;
}

.pipeline__chip--active {
  font-weight: 500;
}

.pipeline__chip--empty {
  opacity: 0.5;
}

.pipeline__chip--more {
  background: transparent;
  border-color: rgba(var(--v-border-color), 0.5);
  border-style: dashed;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.pipeline__dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.pipeline__count {
  font-family: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.pipeline__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  min-width: 16px;
  padding: 0 5px;
  border-radius: 8px;
  font-size: 10.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.pipeline__pill--warn { background: #ffe082; color: #7a4f00; }
.pipeline__pill--over { background: #ef9a9a; color: #7f1d1d; }

.floating-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}
</style>